import React, { useState, useEffect, Fragment } from 'react';
import { styled } from '@mui/material/styles';
import { tableCellClasses } from '@mui/material/TableCell';
import {
    Typography,
    TextField,
    InputAdornment,
    IconButton,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Autocomplete,
    Box,
    Card,
    CardContent,
    Chip,
    Stack,
    Divider
} from '@mui/material';
import {
    Search as SearchIcon,
    Clear as ClearIcon,
    ArrowForwardIosSharp as ArrowForwardIosSharpIcon,
    ArrowDownward as ArrowDownwardIcon,
    Edit as EditIcon,
    Category as CategoryIcon,
    Description as DescriptionIcon
} from '@mui/icons-material';

// Стили для таблицы
const StyledTableCellSpec = styled(TableCell)(({ theme, darkMode }) => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: darkMode ? '#424242' : '#D0D0D0',
        color: darkMode ? '#f5f7fa' : 'black',
        padding: '4px 6px',
        fontSize: '0.75rem',
        fontWeight: 'bold',
        lineHeight: 1.1,
        '&:hover': {
            backgroundColor: darkMode ? '#616161' : '#909090',
            cursor: 'pointer'
        },
        '@media (max-width: 768px)': {
            fontSize: '10px',
            padding: '2px 3px',
        }
    },
    [`&.${tableCellClasses.body}`]: {
        padding: '2px 6px',
        fontSize: '0.7rem',
        lineHeight: 1.1,
        color: darkMode ? '#f5f7fa' : 'inherit',
        '@media (max-width: 768px)': {
            fontSize: '10px',
            padding: '1px 3px',
        }
    },
}));

const StyledTableCellSpecCat = styled(TableCell)(({ theme, darkMode }) => ({
    [`&.${tableCellClasses.body}`]: {
        padding: '2px 6px',
        fontSize: '0.75rem',
        fontWeight: 'bold',
        lineHeight: 1.1,
        color: darkMode ? '#f5f7fa' : 'inherit',
        '@media (max-width: 768px)': {
            fontSize: '10px',
            padding: '1px 3px',
        }
    },
}));

const StyledTableRowSpec = styled(TableRow, {
    shouldForwardProp: (prop) => prop !== 'darkMode'
})(({ theme, darkMode }) => ({
    backgroundColor: darkMode ? '#1a2e1a' : '#f0f8f0',
}));

const SpecsTab = ({ 
    spec, 
    saveToStorage,
    STORAGE_KEYS,
    darkMode = false,
    specs = null,
    type = null,
    mark = null,
    onSpecChange = null,
    canEdit = false
}) => {
    // Внутренние состояния компонента
    const [expandSpec, setExpandedSpec] = useState([]);
    const [searchSpec, setSearchSpec] = useState('');
    const [isEditingSpec, setIsEditingSpec] = useState(false);

    // Загружаем состояние поиска из localStorage при инициализации
    useEffect(() => {
        const savedSearchSpec = loadFromStorage(STORAGE_KEYS.SEARCH_SPEC, '');
        setSearchSpec(savedSearchSpec);
    }, [STORAGE_KEYS.SEARCH_SPEC]);

    // Функция для загрузки из localStorage
    const loadFromStorage = (key, defaultValue) => {
        try {
            const item = localStorage.getItem(key);
            return item ? JSON.parse(item) : defaultValue;
        } catch (error) {
            console.warn('Ошибка загрузки из localStorage:', error);
            return defaultValue;
        }
    };
    // Рекурсивная функция поиска по спецификации
    function searchInSpec(item, searchTerm) {
        if (!searchTerm) return true

        // Разбиваем поисковый запрос на отдельные слова
        const searchWords = searchTerm.toLowerCase().trim().split(/\s+/).filter(word => word.length > 0)

        if (searchWords.length === 0) return true

        // Собираем все текстовые поля элемента в одну строку
        const allText = [
            item.name,
            item.type_marka_docs,
            item.factory_manufacturer,
            item.unit
        ]
            .filter(Boolean)
            .join(' ')
            .toLowerCase()

        // Проверяем, что все слова из поискового запроса содержатся в тексте
        const matchesAllWords = searchWords.every(word => allText.includes(word))

        if (matchesAllWords) return true

        // Рекурсивный поиск в дочерних элементах
        if (item.complect && item.complect.length > 0) {
            return item.complect.some(child => searchInSpec(child, searchTerm))
        }

        return false
    }

    // Функция для автоматического расширения найденных элементов и их родителей
    function expandFoundItems(item, searchTerm, expanded = []) {
        if (!searchTerm || !item.complect) return expanded

        const searchLower = searchTerm.toLowerCase()

        // Проверяем дочерние элементы
        item.complect.forEach(child => {
            const hasMatch = searchInSpec(child, searchTerm)

            if (hasMatch) {
                // Если найденный элемент имеет дочерние элементы, раскрываем его
                if (child.complect && child.complect.length > 0) {
                    if (!expanded.includes(child.id)) {
                        expanded.push(child.id)
                    }
                }

                // Раскрываем родительский элемент, чтобы показать найденный
                if (!expanded.includes(item.id)) {
                    expanded.push(item.id)
                }
            }

            // Рекурсивно проверяем дочерние элементы
            expandFoundItems(child, searchTerm, expanded)
        })

        return expanded
    }

    // Эффект для автоматического расширения при поиске
    useEffect(() => {
        if (searchSpec && spec) {
            const itemsToExpand = []

            // Проходим по всем группам спецификации
            spec.complect_filtered?.forEach(cf => {
                // Проверяем, есть ли совпадения в группе
                const hasMatchesInGroup = cf.complect.some(item => searchInSpec(item, searchSpec))

                if (hasMatchesInGroup) {
                    // Если в группе есть совпадения, раскрываем все элементы группы
                    cf.complect.forEach(item => {
                        expandFoundItems(item, searchSpec, itemsToExpand)
                    })
                }
            })

            // Расширяем найденные элементы
            setExpandedSpec(prev => [...new Set([...prev, ...itemsToExpand])])
        } else if (!searchSpec) {
            // Если поиск очищен, сбрасываем расширенные элементы
            setExpandedSpec([])
        }
    }, [searchSpec, spec])

    function getExpandSpec(spec, level = 0) {
        if (!spec.complect || spec.complect.length === 0 || !expandSpec || !expandSpec.includes(spec.id)) return null

        // Фильтрация дочерних элементов при поиске
        const filteredComplect = searchSpec ?
            spec.complect.filter(item => searchInSpec(item, searchSpec)) :
            spec.complect

        return filteredComplect.map((c, y) => {
            let mass = (parseFloat(c.massa_1ed_kg) * parseFloat(c.count)).toString()
            mass = mass.includes('.') ? mass.split('.') : [mass]
            if (mass.length > 1) {
                mass[1] = mass[1].slice(0, 4)
            }
            return (
                <Fragment key={`fragment_${spec.id}_${c.id || y}`}>
                    <StyledTableRowSpec darkMode={darkMode}>
                        <StyledTableCellSpec darkMode={darkMode} style={{ width: '28px', padding: '2px' }}>
                            {c.complect && c.complect.length > 0 && (
                                <Button
                                    onClick={(event) => {
                                        event.preventDefault();
                                        event.stopPropagation();
                                        setExpandedSpec(prev => prev.includes(c.id) ? prev.filter(id => id !== c.id) : [...prev, c.id])
                                    }}
                                    style={{ minWidth: '20px', padding: '2px', marginLeft: `${level * 10}px` }}
                                    sx={{
                                        '& .MuiSvgIcon-root': { fontSize: { xs: '12px', md: '14px' } },
                                        color: darkMode ? '#64b5f6' : '#1976d2',
                                        '&:hover': {
                                            backgroundColor: darkMode ? 'rgba(100, 181, 246, 0.1)' : 'rgba(25, 118, 210, 0.1)',
                                        }
                                    }}
                                >
                                    {expandSpec.includes(c.id) ? <ArrowDownwardIcon fontSize="small" /> : <ArrowForwardIosSharpIcon fontSize="small" />}
                                </Button>
                            )}
                        </StyledTableCellSpec>
                        <StyledTableCellSpec
                            darkMode={darkMode}
                            style={{ paddingLeft: `${level * 10}px` }}
                        >
                            {c.name}
                        </StyledTableCellSpec>
                        <StyledTableCellSpec darkMode={darkMode}>{c.type_marka_docs}</StyledTableCellSpec>
                        <StyledTableCellSpec darkMode={darkMode}>{c.factory_manufacturer}</StyledTableCellSpec>
                        <StyledTableCellSpec darkMode={darkMode} style={{ textAlign: 'center' }}>
                            {parseFloat(c.count).toString().replace('.', ',')} {c.unit}
                        </StyledTableCellSpec>
                        <StyledTableCellSpec darkMode={darkMode} style={{ textAlign: 'center' }}>
                            {mass.join(',')}
                        </StyledTableCellSpec>
                    </StyledTableRowSpec>
                    {getExpandSpec(c, level + 1)}
                </Fragment>
            )
        })
    }

    // Обработчик изменения поиска с сохранением в localStorage
    const handleSearchChange = (event) => {
        const searchValue = event.target.value;
        setSearchSpec(searchValue);
        saveToStorage(STORAGE_KEYS.SEARCH_SPEC, searchValue);
    };

    // Подготовка данных для Autocomplete
    const getGroupedOptions = () => {
        if (!specs || !type) return [];

        if (type === 'pillars') {
            const jointPoles = specs.pillars
                ?.filter(t => t.category?.includes('0,4') && t.category?.includes('6(10)'))
                .sort((a, b) => a.name.localeCompare(b.name))
                .map(t => ({ ...t, group: 'Опоры совместного подвеса' })) || [];

            const lowVoltagePoles = specs.pillars
                ?.filter(t => t.category?.includes('0,4') && !t.category?.includes('6(10)'))
                .sort((a, b) => a.name.localeCompare(b.name))
                .map(t => ({ ...t, group: 'Опоры 0,4кв' })) || [];

            const highVoltagePoles = specs.pillars
                ?.filter(t => !t.category?.includes('0,4') && t.category?.includes('6(10)'))
                .sort((a, b) => a.name.localeCompare(b.name))
                .map(t => ({ ...t, group: 'Опоры 6(10)кв' })) || [];

            const otherPoles = specs.pillars
                ?.filter(t => !t.category?.includes('0,4') && !t.category?.includes('6(10)'))
                .sort((a, b) => a.name.localeCompare(b.name))
                .map(t => ({ ...t, group: 'Другие опоры' })) || [];

            return [...jointPoles, ...lowVoltagePoles, ...highVoltagePoles, ...otherPoles];
        }
        if (type === 'zvz') {
            return specs.zvz?.map(t => ({ ...t, group: 'Адаптеры ВЗ' })) || [];
        }
        if (type === 'opn') {
            return specs.opn?.map(t => ({ ...t, group: 'ОПН' })) || [];
        }
        if (type === 'rlnd') {
            return specs.rlnd?.map(t => ({ ...t, group: 'РЛНД' })) || [];
        }
        if (type === 'transformers' || type === 'transformer') {
            return specs.transformers?.map(t => ({ ...t, group: 'Трансформаторы' })) || [];
        }
        if (type === 'runn') {
            return specs.runn?.map(t => ({ ...t, group: 'РУНН' })) || [];
        }
        return [];
    };

    const options = getGroupedOptions();
    const currentValue = spec ? options.find(opt => opt.id === spec.id) || null : null;

    // Обработчик изменения спецификации
    const handleSpecChange = (event, newValue) => {
        if (event) {
            event.stopPropagation();
        }
        if (newValue && onSpecChange) {
            onSpecChange(newValue.id);
            setIsEditingSpec(false);
        } else if (!newValue) {
            setIsEditingSpec(false);
        }
    };

    // Если спецификация не указана, показываем возможность её выбрать
    if (!spec) {
        const options = getGroupedOptions();
        
        // Если есть права на редактирование и доступны опции, показываем Autocomplete
        if (canEdit && specs && type && options.length > 0) {
            return (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'stretch',
                        p: 2,
                        gap: 1.5,
                        borderRadius: 2,
                        backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.04)' : 'rgba(0, 0, 0, 0.02)',
                        border: '1px solid',
                        borderColor: darkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)',
                    }}
                >
                    <Typography
                        variant="body2"
                        sx={{
                            color: darkMode ? 'rgba(255, 255, 255, 0.65)' : 'rgba(0, 0, 0, 0.55)',
                            fontSize: '0.8rem',
                            fontWeight: 500,
                            letterSpacing: '0.01em',
                            mb: 0.5,
                        }}
                    >
                        Спецификация не указана. Выберите спецификацию:
                    </Typography>
                    <Box onClick={(e) => e.stopPropagation()}>
                        <Autocomplete
                            size="small"
                            sx={{
                                minWidth: { xs: 250, md: 400 },
                                '& .MuiInputBase-input': {
                                    fontSize: '0.8rem',
                                    color: darkMode ? '#f5f7fa !important' : '#333 !important',
                                    '-webkit-text-fill-color': darkMode ? '#f5f7fa !important' : '#333 !important',
                                },
                                '& .MuiInputLabel-root': {
                                    fontSize: '0.8rem',
                                    color: darkMode ? '#bbb' : 'rgba(0,0,0,0.6)',
                                },
                                '& .MuiOutlinedInput-root': {
                                    backgroundColor: darkMode ? '#1e1e1e' : '#f8f9fa',
                                    '& fieldset': {
                                        borderColor: darkMode ? 'rgba(255,255,255,0.23)' : 'rgba(0,0,0,0.23)',
                                    },
                                    '&:hover fieldset': {
                                        borderColor: darkMode ? 'rgba(100, 181, 246, 0.4)' : 'rgba(25, 118, 210, 0.4)',
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: darkMode ? '#64b5f6' : '#1976d2',
                                        borderWidth: '2px',
                                    },
                                },
                            }}
                            options={options}
                            groupBy={(option) => option.group}
                            getOptionLabel={(option) => option.name || ''}
                            getOptionKey={(option) => option.id ?? option.name}
                            isOptionEqualToValue={(option, value) => option.id === value?.id}
                            value={null}
                            onChange={(event, newValue) => {
                                if (event) event.stopPropagation();
                                if (newValue && onSpecChange) {
                                    onSpecChange(newValue.id);
                                }
                            }}
                            onBlur={(e) => {
                                e.stopPropagation();
                            }}
                            onOpen={(e) => {
                                if (e) e.stopPropagation();
                            }}
                            onClose={(e, reason) => {
                                if (e) e.stopPropagation();
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Выберите спецификацию"
                                    placeholder="Начните вводить название..."
                                    sx={{
                                        '& .MuiInputBase-input': {
                                            fontSize: '0.8rem',
                                            color: darkMode ? '#f5f7fa' : '#333',
                                        },
                                    }}
                                />
                            )}
                            slotProps={{
                                paper: {
                                    sx: {
                                        backgroundColor: darkMode ? '#2a2a2a' : '#fff',
                                        color: darkMode ? '#f5f7fa' : '#333',
                                        boxShadow: darkMode
                                            ? '0 4px 20px rgba(0,0,0,0.4)'
                                            : '0 4px 20px rgba(0,0,0,0.1)',
                                        borderRadius: 1.5,
                                        '& .MuiAutocomplete-option': {
                                            fontSize: '0.8rem',
                                            padding: '2px 12px',
                                            minHeight: 32,
                                        },
                                        '& .MuiAutocomplete-groupLabel': {
                                            fontSize: '0.75rem',
                                            fontWeight: 600,
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.05em',
                                            color: darkMode ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.55)',
                                            minHeight: 28,
                                            padding: '0px 12px',
                                        },
                                    }
                                }
                            }}
                        />
                    </Box>
                </Box>
            );
        }
        
        // Если нет прав на редактирование или нет опций, показываем только текст
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '200px',
                    p: 3,
                    textAlign: 'center'
                }}
            >
                <Typography
                    variant="body1"
                    sx={{
                        color: darkMode ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)',
                        fontSize: '14px'
                    }}
                >
                    Спецификация не указана
                </Typography>
            </Box>
        );
    }
    console.log(canEdit, specs, type)
    return (
        <Box
            sx={{
                maxHeight: '300px',
                overflowY: 'auto',
                scrollbarWidth: 'thin',
                '&::-webkit-scrollbar': {
                    width: '8px',
                    height: '8px',
                },
                '&::-webkit-scrollbar-track': {
                    background: darkMode ? '#2a2a2a' : '#f1f1f1',
                    borderRadius: '4px',
                },
                '&::-webkit-scrollbar-thumb': {
                    background: darkMode ? '#555' : '#c1c1c1',
                    borderRadius: '4px',
                    '&:hover': {
                        background: darkMode ? '#777' : '#a8a8a8',
                    },
                },
                scrollbarColor: darkMode ? '#555 #2a2a2a' : '#c1c1c1 #f1f1f1',
            }}
        >
            {/* Заголовок спецификации */}
            <Card
                variant="outlined"
                sx={{
                    mb: 0.5,
                    backgroundColor: darkMode ? 'rgba(100, 181, 246, 0.05)' : 'rgba(25, 118, 210, 0.05)',
                    borderColor: darkMode ? 'rgba(100, 181, 246, 0.2)' : 'rgba(25, 118, 210, 0.2)',
                }}
            >
                <CardContent sx={{ py: 0.3, px: 1.5, '&:last-child': { pb: 0.3 } }}>
                    <Stack spacing={0.2}>
                        {!isEditingSpec ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                                <Typography
                                    variant="subtitle1"
                                    
                                    sx={{
                                        fontWeight: 600,
                                        fontSize: { xs: '0.8rem', sm: '0.9rem' },
                                        color: darkMode ? '#f5f7fa' : '#333',
                                        lineHeight: 1.1,
                                        flex: 1,
                                        minWidth: 0
                                    }}
                                >
                                    {spec.name}
                                </Typography>
                                {canEdit && specs && type && options.length > 1 && (
                                    <IconButton
                                        size="small"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setIsEditingSpec(true);
                                        }}
                                        sx={{
                                            padding: '4px',
                                            color: darkMode ? '#64b5f6' : '#1976d2',
                                            '&:hover': {
                                                backgroundColor: darkMode ? 'rgba(100, 181, 246, 0.15)' : 'rgba(25, 118, 210, 0.15)',
                                                transform: 'scale(1.1)',
                                            },
                                            transition: 'all 0.2s ease-in-out',
                                        }}
                                    >
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                )}
                            </Box>
                        ) : (
                            <Box onClick={(e) => e.stopPropagation()}>
                                <Autocomplete
                                    size="small"
                                    sx={{
                                        minWidth: { xs: 250, md: 400 },
                                        '& .MuiInputBase-input': {
                                            color: darkMode ? '#f5f7fa !important' : '#333 !important',
                                            '-webkit-text-fill-color': darkMode ? '#f5f7fa !important' : '#333 !important',
                                        },
                                        '& .MuiInputLabel-root': {
                                            color: darkMode ? '#bbb' : 'rgba(0,0,0,0.6)',
                                        },
                                        '& .MuiOutlinedInput-root': {
                                            backgroundColor: darkMode ? '#1e1e1e' : '#f8f9fa',
                                            '& fieldset': {
                                                borderColor: darkMode ? 'rgba(255,255,255,0.23)' : 'rgba(0,0,0,0.23)',
                                            },
                                            '&:hover fieldset': {
                                                borderColor: darkMode ? 'rgba(100, 181, 246, 0.4)' : 'rgba(25, 118, 210, 0.4)',
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: darkMode ? '#64b5f6' : '#1976d2',
                                            },
                                        },
                                        '& .MuiAutocomplete-endAdornment': {
                                            color: darkMode ? '#bbb !important' : 'rgba(0,0,0,0.54) !important',
                                        },
                                        '& .MuiAutocomplete-clearIndicator': {
                                            color: darkMode ? '#bbb !important' : 'rgba(0,0,0,0.54) !important',
                                            '&:hover': {
                                                color: darkMode ? '#f5f7fa !important' : 'rgba(0,0,0,0.87) !important',
                                            },
                                        },
                                        '& .MuiAutocomplete-popupIndicator': {
                                            color: darkMode ? '#bbb !important' : 'rgba(0,0,0,0.54) !important',
                                            '&:hover': {
                                                color: darkMode ? '#64b5f6 !important' : '#1976d2 !important',
                                            },
                                        },
                                    }}
                                    options={options}
                                    groupBy={(option) => option.group}
                                    getOptionLabel={(option) => option.name || ''}
                                    getOptionKey={(option) => option.id ?? option.name}
                                    isOptionEqualToValue={(option, value) => option.id === value?.id}
                                    value={currentValue}
                                    onChange={handleSpecChange}
                                    onBlur={(e) => {
                                        e.stopPropagation();
                                        setIsEditingSpec(false);
                                    }}
                                    open={isEditingSpec}
                                    onOpen={(e) => {
                                        if (e) e.stopPropagation();
                                        setIsEditingSpec(true);
                                    }}
                                    onClose={(e, reason) => {
                                        if (e) e.stopPropagation();
                                        if (reason !== 'selectOption') {
                                            setIsEditingSpec(false);
                                        }
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Тип опоры"
                                            placeholder="Выберите тип опоры"
                                            sx={{
                                                '& .MuiInputBase-input': {
                                                    fontSize: { xs: '0.75rem', md: '0.8rem' },
                                                    color: darkMode ? '#f5f7fa' : '#333',
                                                },
                                                '& .MuiInputLabel-root': {
                                                    color: darkMode ? '#bbb' : 'rgba(0,0,0,0.6)',
                                                },
                                                '& .MuiOutlinedInput-root': {
                                                    backgroundColor: darkMode ? '#1e1e1e' : '#f8f9fa',
                                                    '& fieldset': {
                                                        color: darkMode ? '#f5f7fa' : '#333',
                                                        borderColor: darkMode ? 'rgba(255,255,255,0.23)' : 'rgba(0,0,0,0.23)',
                                                    },
                                                    '&:hover fieldset': {
                                                        borderColor: darkMode ? 'rgba(100, 181, 246, 0.4)' : 'rgba(25, 118, 210, 0.4)',
                                                    },
                                                    '&.Mui-focused fieldset': {
                                                        borderColor: darkMode ? '#64b5f6' : '#1976d2',
                                                    },
                                                },
                                            }}
                                        />
                                    )}
                                    slotProps={{
                                        paper: {
                                            sx: {
                                                zIndex: 9999,
                                                backgroundColor: darkMode ? '#1e1e1e' : '#fff',
                                                '& .MuiAutocomplete-option': {
                                                    fontSize: { xs: '0.75rem', md: '0.8rem' },
                                                    padding: { xs: '2px 8px', md: '2px 12px' },
                                                    minHeight: { xs: '18px', md: '24px' },
                                                    color: darkMode ? '#f5f7fa' : 'inherit',
                                                    '&:hover': {
                                                        backgroundColor: darkMode ? 'rgba(100, 181, 246, 0.1)' : 'rgba(25, 118, 210, 0.1)',
                                                    },
                                                    '&[aria-selected="true"]': {
                                                        backgroundColor: darkMode ? 'rgba(100, 181, 246, 0.2)' : 'rgba(25, 118, 210, 0.2)',
                                                    },
                                                },
                                            },
                                        },
                                    }}
                                />
                            </Box>
                        )}
                        {/* <Divider sx={{ my: 0.5 }} /> */}
                        <Stack direction="row" alignItems="center" flexWrap="wrap" gap={0.5}>
                            {spec.type_marka_docs && (
                                <Chip
                                    icon={<DescriptionIcon />}
                                    label={spec.type_marka_docs}
                                    size="small"
                                    sx={{
                                        fontSize: { xs: '0.65rem', sm: '0.7rem' },
                                        height: '20px',
                                        backgroundColor: darkMode ? 'rgba(100, 181, 246, 0.1)' : 'rgba(25, 118, 210, 0.1)',
                                        color: darkMode ? '#64b5f6' : '#1976d2',
                                        border: `1px solid ${darkMode ? 'rgba(100, 181, 246, 0.3)' : 'rgba(25, 118, 210, 0.3)'}`,
                                        '& .MuiChip-icon': {
                                            fontSize: '14px',
                                            color: darkMode ? '#64b5f6' : '#1976d2',
                                        }
                                    }}
                                />
                            )}
                            {spec.category && (
                                <Chip
                                    icon={<CategoryIcon />}
                                    label={spec.category}
                                    size="small"
                                    sx={{
                                        fontSize: { xs: '0.65rem', sm: '0.7rem' },
                                        height: '20px',
                                        backgroundColor: darkMode ? 'rgba(76, 175, 80, 0.1)' : 'rgba(46, 125, 50, 0.1)',
                                        color: darkMode ? '#4caf50' : '#2e7d32',
                                        border: `1px solid ${darkMode ? 'rgba(76, 175, 80, 0.3)' : 'rgba(46, 125, 50, 0.3)'}`,
                                        '& .MuiChip-icon': {
                                            fontSize: '14px',
                                            color: darkMode ? '#4caf50' : '#2e7d32',
                                        }
                                    }}
                                />
                            )}
                        </Stack>
                    </Stack>
                </CardContent>
            </Card>

            {/* Поле поиска */}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'end',
                    alignItems: 'center',
                    mb: 0.5,
                    gap: 1,
                    flexWrap: 'wrap'
                }}
            >
                {searchSpec && (
                    <Chip
                        label={`Найдено: ${(() => {
                            let count = 0
                            spec.complect_filtered?.forEach(cf => {
                                cf.complect.forEach(item => {
                                    if (searchInSpec(item, searchSpec)) count++
                                })
                            })
                            return count
                        })()}`}
                        size="small"
                        sx={{
                            fontSize: '0.7rem',
                            height: '24px',
                            backgroundColor: darkMode ? 'rgba(255, 183, 77, 0.15)' : 'rgba(255, 183, 77, 0.15)',
                            color: darkMode ? '#ffb74d' : '#f57c00',
                            fontWeight: 600,
                        }}
                    />
                )}
                <TextField
                    size="small"
                    placeholder="Поиск по спецификации..."
                    value={searchSpec}
                    onChange={handleSearchChange}
                    variant="outlined"
                    sx={{
                        flex: 1,
                        minWidth: { xs: '100%', sm: '200px' },
                        maxWidth: { xs: '100%', sm: '300px' },
                        '& .MuiOutlinedInput-root': {
                            height: '24px',
                            backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
                            '& input': {
                                padding: '3px 12px',
                                fontSize: '0.85rem'
                            },
                            '& fieldset': {
                                borderColor: darkMode ? 'rgba(255, 255, 255, 0.23)' : 'rgba(0, 0, 0, 0.23)',
                            },
                            '&:hover fieldset': {
                                borderColor: darkMode ? 'rgba(100, 181, 246, 0.5)' : 'rgba(25, 118, 210, 0.5)',
                            },
                            '&.Mui-focused fieldset': {
                                borderColor: darkMode ? '#64b5f6' : '#1976d2',
                            },
                        }
                    }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon
                                    sx={{
                                        color: darkMode ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.54)',
                                        fontSize: '18px'
                                    }}
                                />
                            </InputAdornment>
                        ),
                        endAdornment: searchSpec && (
                            <InputAdornment position="end">
                                <IconButton
                                    size="small"
                                    onClick={() => {
                                        setSearchSpec('');
                                        saveToStorage(STORAGE_KEYS.SEARCH_SPEC, '');
                                    }}
                                    edge="end"
                                    sx={{
                                        padding: '4px',
                                        color: darkMode ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.54)',
                                        '&:hover': {
                                            color: darkMode ? '#f5f7fa' : '#000',
                                            backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
                                        }
                                    }}
                                >
                                    <ClearIcon fontSize="small" />
                                </IconButton>
                            </InputAdornment>
                        )
                    }}
                />
            </Box>

            <TableContainer
                component={Paper}
                elevation={0}
                sx={{
                    mb: 1,
                    backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.01)',
                    border: `1px solid ${darkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)'}`,
                    borderRadius: '8px',
                    overflow: 'hidden',
                    '& .MuiTable-root': {
                        borderCollapse: 'separate',
                        borderSpacing: 0,
                    },
                }}
            >
                <Table size="small" sx={{
                    // minWidth: 800,
                    '& .MuiTableCell-root': {
                        borderBottom: darkMode ? '1px solid rgba(255, 255, 255, 0.05)' : '1px solid rgba(0, 0, 0, 0.08)',
                        borderRight: darkMode ? '1px solid rgba(255, 255, 255, 0.03)' : '1px solid rgba(0, 0, 0, 0.05)',
                    },
                    '& .MuiTableCell-root:last-child': {
                        borderRight: 'none',
                    }
                }} aria-label="specs table">
                    <TableHead>
                        <StyledTableRowSpec darkMode={darkMode}>
                            <StyledTableCellSpec align="center" style={{ width: '20px' }} darkMode={darkMode}></StyledTableCellSpec>
                            <StyledTableCellSpec align="center" darkMode={darkMode}>Наименование</StyledTableCellSpec>
                            <StyledTableCellSpec align="center" darkMode={darkMode}>Тип, марка, обозначение документа, опросного листа</StyledTableCellSpec>
                            <StyledTableCellSpec align="center" darkMode={darkMode}>Завод изготовитель</StyledTableCellSpec>
                            <StyledTableCellSpec align="center" darkMode={darkMode}>Количество</StyledTableCellSpec>
                            <StyledTableCellSpec align="center" darkMode={darkMode}>Масса ед., кг.</StyledTableCellSpec>
                        </StyledTableRowSpec>
                    </TableHead>
                    <TableBody>
                        {
                            spec.complect_filtered?.map((cf, cfIndex) => {
                                // Фильтрация элементов на основе поиска
                                const filteredComplect = cf.complect.filter(item => searchInSpec(item, searchSpec))

                                // Если нет совпадений в этой группе, не показываем её
                                if (searchSpec && filteredComplect.length === 0) {
                                    return null
                                }

                                return (
                                    <Fragment key={`cf_${cfIndex}_${cf.type}`}>
                                        {/* Заголовок категории */}
                                        <StyledTableRowSpec darkMode={darkMode}>
                                            <StyledTableCellSpecCat
                                                colSpan={6}
                                                align="center"
                                                darkMode={darkMode}
                                                sx={{
                                                    fontWeight: 700,
                                                    backgroundColor: darkMode ? 'rgba(76, 175, 80, 0.15)' : 'rgba(76, 175, 80, 0.1)',
                                                    padding: '4px 12px',
                                                    fontSize: { xs: '0.7rem', sm: '0.8rem' },
                                                    color: darkMode ? '#4caf50' : '#2e7d32',
                                                    borderTop: `2px solid ${darkMode ? 'rgba(76, 175, 80, 0.3)' : 'rgba(76, 175, 80, 0.3)'}`,
                                                    borderBottom: `1px solid ${darkMode ? 'rgba(76, 175, 80, 0.2)' : 'rgba(76, 175, 80, 0.2)'}`,
                                                }}
                                            >
                                                {cf.type}
                                            </StyledTableCellSpecCat>
                                        </StyledTableRowSpec>

                                        {/* Элементы категории */}
                                        {(!searchSpec ? cf.complect : filteredComplect).map((c, i) => {
                                            // Проверяем соответствие поиску для отдельного элемента
                                            if (searchSpec && !searchInSpec(c, searchSpec)) {
                                                return null
                                            }

                                            let mass = (parseFloat(c.massa_1ed_kg) * parseFloat(c.count)).toString();
                                            mass = mass.includes('.') ? mass.split('.') : [mass];
                                            if (mass.length > 1) {
                                                mass[1] = mass[1].slice(0, 4);
                                            }

                                            return (
                                                <Fragment key={`fragment_${cfIndex}_${i}`}>
                                                    <StyledTableRowSpec darkMode={darkMode}>
                                                        <StyledTableCellSpec darkMode={darkMode} style={{ width: '28px', padding: '2px' }}>
                                                            {c.complect && c.complect.length > 0 && (
                                                                <Button
                                                                    onClick={(event) => {
                                                                        event.preventDefault();
                                                                        event.stopPropagation();
                                                                        setExpandedSpec(prev => prev.includes(c.id) ? prev.filter(id => id !== c.id) : [...prev, c.id])
                                                                    }}
                                                                    style={{ minWidth: '20px', padding: '2px', marginLeft: '0px' }}
                                                                    sx={{
                                                                        '& .MuiSvgIcon-root': { fontSize: { xs: '10px', md: '12px' } },
                                                                        color: darkMode ? '#64b5f6' : '#1976d2',
                                                                        '&:hover': {
                                                                            backgroundColor: darkMode ? 'rgba(100, 181, 246, 0.1)' : 'rgba(25, 118, 210, 0.1)',
                                                                        }
                                                                    }}
                                                                >
                                                                    {expandSpec.includes(c.id) ? <ArrowDownwardIcon fontSize="small" /> : <ArrowForwardIosSharpIcon fontSize="small" />}
                                                                </Button>
                                                            )}
                                                        </StyledTableCellSpec>
                                                        <StyledTableCellSpec
                                                            darkMode={darkMode}
                                                            style={{ paddingLeft: '0px' }}
                                                        >
                                                            {c.name}
                                                        </StyledTableCellSpec>
                                                        <StyledTableCellSpec darkMode={darkMode}>{c.type_marka_docs}</StyledTableCellSpec>
                                                        <StyledTableCellSpec darkMode={darkMode}>{c.factory_manufacturer}</StyledTableCellSpec>
                                                        <StyledTableCellSpec darkMode={darkMode} style={{ textAlign: 'center' }}>
                                                            {parseFloat(c.count).toString().replace('.', ',')} {c.unit}
                                                        </StyledTableCellSpec>
                                                        <StyledTableCellSpec darkMode={darkMode} style={{ textAlign: 'center' }}>
                                                            {mass.join(',')}
                                                        </StyledTableCellSpec>
                                                    </StyledTableRowSpec>
                                                    {getExpandSpec(c, 1)}
                                                </Fragment>
                                            )
                                        })}
                                    </Fragment>
                                )
                            }).filter(Boolean)
                        }
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

export default SpecsTab;
